// vg-binding: P7.D-69
// vg-binding: P7.D-70
//
// apps/vendor/src/features/print-queue/api.ts
//
// Phase 7 Wave 14 Task 54 — TanStack Query hooks for printer CRUD +
// Kanban print-queue + reprint flow.

import { csrfFetch } from '../../lib/api/csrfFetch';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

export type PrinterType =
  | 'dtg'
  | 'dtf'
  | 'sublimation'
  | 'embroidery'
  | 'screen_print'
  | 'heat_press'
  | 'cutter'
  | 'sewing'
  | 'laser'
  | 'uv_print'
  | 'other';

export interface Printer {
  printer_id: string;
  name: string;
  model: string | null;
  location: string | null;
  printer_type: PrinterType;
  is_active: boolean;
  capacity_per_day: number | null;
}

export type ItemLifecycleStatus =
  | 'pending'
  | 'accepted'
  | 'in_production'
  | 'quality_check'
  | 'ready_to_ship'
  | 'shipped'
  | 'completed'
  | 'cancelled';

export interface PrintQueueAssignment {
  assignment_id: string;
  assigned_at: number;
  // UAT-12 expansion (2026-05-16) — per-card metadata so the vendor can
  // control the production lifecycle without leaving the page.
  order_id?: string;
  item_id?: string;
  item_version?: number;
  item_status?: ItemLifecycleStatus;
  sla_deadline?: number;
  quantity?: number;
}

export interface PrintQueueColumn {
  printer_id: string;
  name: string;
  printer_type: PrinterType;
  assignments: PrintQueueAssignment[];
}

export type ReprintReason =
  | 'qc_fail'
  | 'damaged'
  | 'customer_request'
  | 'misalignment'
  | 'color_deviation'
  | 'material_defect'
  | 'size_wrong'
  | 'packaging_damage'
  | 'other';

interface ApiOk<T> {
  ok: true;
  data: T;
}
interface ApiErr {
  ok: false;
  error: { code: string; message: string; user_message?: string };
}

export class PrintQueueApiError extends Error {
  code: string;
  statusCode: number;
  constructor(code: string, message: string, statusCode: number) {
    super(message);
    this.code = code;
    this.statusCode = statusCode;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiOk<T> | ApiErr;
  if (!body.ok) {
    throw new PrintQueueApiError(body.error.code, body.error.user_message ?? body.error.message, res.status);
  }
  return body.data;
}

export function usePrintersList(): UseQueryResult<{ rows: Printer[] }, PrintQueueApiError> {
  return useQuery({
    queryKey: ['vendor', 'printers'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/printers', { credentials: 'include' });
      return unwrap<{ rows: Printer[] }>(res);
    },
  });
}

export function usePrintQueue(): UseQueryResult<{ columns: PrintQueueColumn[] }, PrintQueueApiError> {
  return useQuery({
    queryKey: ['vendor', 'print-queue'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/print-queue', { credentials: 'include' });
      return unwrap<{ columns: PrintQueueColumn[] }>(res);
    },
    refetchInterval: 30_000,
  });
}

interface PrinterCreateInput {
  name: string;
  model?: string;
  location?: string;
  printer_type: PrinterType;
  capacity_per_day?: number;
}

export function useCreatePrinter(): UseMutationResult<{ printer_id: string }, PrintQueueApiError, PrinterCreateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PrinterCreateInput) => {
      const res = await csrfFetch('/api/v1/vendor/printers', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return unwrap<{ printer_id: string }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor', 'printers'] });
      void qc.invalidateQueries({ queryKey: ['vendor', 'print-queue'] });
    },
  });
}

// dbg (2026-07-07) UAT #2075 — delete a printer so the vendor can reach the
// Kanban empty-state. BE returns 409 PRINTER_HAS_ASSIGNMENTS if still in use.
export function useDeletePrinter(): UseMutationResult<
  { deleted: true },
  PrintQueueApiError,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (printerId: string) => {
      const res = await csrfFetch(
        `/api/v1/vendor/printers/${encodeURIComponent(printerId)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      return unwrap<{ deleted: true }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor', 'printers'] });
      void qc.invalidateQueries({ queryKey: ['vendor', 'print-queue'] });
    },
  });
}

interface AssignPrinterInput {
  orderId: string;
  itemId: string;
  printer_id: string;
}

export function useAssignPrinter(): UseMutationResult<
  { assigned: true },
  PrintQueueApiError,
  AssignPrinterInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, itemId, printer_id }: AssignPrinterInput) => {
      const res = await csrfFetch(
        `/api/v1/vendor/orders/${orderId}/items/${itemId}/assign-printer`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ printer_id }),
        },
      );
      return unwrap<{ assigned: true }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor', 'print-queue'] });
    },
  });
}

interface ReprintInput {
  orderId: string;
  itemId: string;
  reason: ReprintReason;
  notes?: string;
}

export function useReprint(): UseMutationResult<
  { reprint_id: string; reprint_count: number; sla_extended_minutes: number },
  PrintQueueApiError,
  ReprintInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, itemId, reason, notes }: ReprintInput) => {
      const res = await csrfFetch(`/api/v1/vendor/orders/${orderId}/items/${itemId}/reprint`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, notes }),
      });
      return unwrap<{ reprint_id: string; reprint_count: number; sla_extended_minutes: number }>(
        res,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor', 'orders'] });
    },
  });
}
